import { RefObject, useCallback } from 'react';
import { Button } from '@Components/Button';
import { Appearance, Design, Size } from '@Components/designConstants';
import { useSetError } from '../../../redux/modals';
import { AuditReport, ScanSource } from '../digital-audit-constants';
import { downloadBlob, renderElementAsHtmlDocument } from '../digital-audit-utils';
import { renderBrief } from '../engine/martech-scanner-utils';
import { renderJourneyMarkdown } from '../engine/journey-mapper-utils';

interface ExportMenuProps {
  report: AuditReport;
  tab: { key: string; label: string };
  tabContentRef: RefObject<HTMLDivElement | null>;
}

export const ExportMenu = ({ report, tab, tabContentRef }: ExportMenuProps) => {
  const setError = useSetError();

  const handleDownloadBrief = useCallback(() => {
    // DEV-24211: scanSource lives on AuditReport, not ScanResult, so it has
    // to be passed explicitly - otherwise the brief's methodology footer
    // still describes a static scan after a full-scan merge.
    const isFullScan = report.scanSource === ScanSource.FULL;
    const markdown = `${renderBrief(report.scanResult, isFullScan)}\n${renderJourneyMarkdown(report.journey)}\n`;
    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8;' });
    downloadBlob(blob, `${report.scanResult.domain}_brief.md`);
  }, [report]);

  const handleDownloadTab = useCallback(() => {
    try {
      const tabContent = tabContentRef.current;
      if (!tabContent) throw new Error('The tab has not finished rendering yet.');
      const { domain } = report.scanResult;
      const html = renderElementAsHtmlDocument(tabContent, `${domain} – ${tab.label}`);
      downloadBlob(
        new Blob([html], { type: 'text/html;charset=utf-8' }),
        `${domain}_${tab.key}.html`
      );
    } catch (e) {
      setError({ message: `Couldn't export this tab: ${e.message}`, reportError: e });
    }
  }, [report, tab, tabContentRef, setError]);

  return (
    <div className='digital-audit-export-menu'>
      <Button
        appearance={Appearance.OUTLINED}
        design={Design.NEUTRAL}
        size={Size.SM}
        onClick={handleDownloadBrief}
      >
        Download audit brief
      </Button>
      <Button
        appearance={Appearance.OUTLINED}
        design={Design.NEUTRAL}
        size={Size.SM}
        onClick={handleDownloadTab}
      >
        Download this tab
      </Button>
    </div>
  );
};
